import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { InstaQLEntity } from "@instantdb/react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  Link,
  MessageCircle,
  X,
} from "lucide-react";
import { viewChatConversationLink } from "../../../constants.ts";
import {
  approveAdminSourceRequest,
  fetchAdminSourceEvents,
  rejectAdminSourceRequest,
} from "../adminApi.ts";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import type { EventEntry } from "../../../src/fields.ts";
import { stripTrailingNumeral } from "../../../src/groupNameUtil.ts";
import type schema from "../../../instant.schema.ts";

type SourceRequest = InstaQLEntity<typeof schema, "sourceRequests">;

const statuses = ["pending", "approved", "rejected"] as const;
type Status = typeof statuses[number];

type Toast = {
  id: number;
  kind: "info" | "success" | "error";
  title: string;
  description?: string;
  pending?: boolean;
};

const formatDate = (timestamp?: number) =>
  timestamp
    ? new Date(timestamp).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "-";

const getBucketLabel = (timestamp?: number) => {
  if (!timestamp) return "Older";
  const now = new Date();
  const date = new Date(timestamp);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgoStart = todayStart - 6 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgoStart = todayStart - 29 * 24 * 60 * 60 * 1000;

  const t = date.getTime();
  if (t >= todayStart) {
    return "Today";
  }
  if (t >= yesterdayStart) {
    return "Yesterday";
  }
  if (t >= sevenDaysAgoStart) {
    return "This Week";
  }
  if (t >= thirtyDaysAgoStart) {
    return "This Month";
  }
  return "Older";
};

const groupClustersByDate = (clusters: SourceRequest[][]) =>
  clusters.reduce<{ label: string; clusters: SourceRequest[][] }[]>(
    (acc, cluster) => {
      const primary = cluster[0];
      const label = getBucketLabel(primary?.createdAt);
      const existing = acc.find((b) => b.label === label);
      if (existing) {
        existing.clusters.push(cluster);
      } else {
        acc.push({ label, clusters: [cluster] });
      }
      return acc;
    },
    [],
  );

const statusLabelClass: Record<Status, string> = {
  pending: "text-amber-200 border-amber-500/30 bg-amber-500/10",
  approved: "text-emerald-200 border-emerald-500/30 bg-emerald-500/10",
  rejected: "text-red-200 border-red-500/30 bg-red-500/10",
};

const kindLabel = (kind: string) =>
  kind === "whatsapp-group"
    ? "WhatsApp group"
    : kind === "facebook-source"
    ? "Facebook group/page"
    : kind === "link-scrape-source"
    ? "Website source"
    : "Instagram page";

const payloadSummary = (request: SourceRequest) =>
  request.kind === "instagram-page"
    ? request.instagramUsername
      ? `@${request.instagramUsername}`
      : request.sourceUrl
    : (request.payload as { title?: string })?.title ||
      request.title ||
      request.sourceUrl;

const whatsappDecision = (request: SourceRequest) =>
  request.kind === "whatsapp-group" &&
    (request.payload as { kind?: string; recommendJoin?: boolean }).kind ===
      "whatsapp-group"
    ? (request.payload as { recommendJoin: boolean }).recommendJoin
      ? "Recommended"
      : "Skip"
    : null;

const whatsappChatTitle = (request: SourceRequest) =>
  request.kind === "whatsapp-group" && request.discoverySource === "spy-bot"
    ? (request.payload as { chatTitle?: string }).chatTitle
    : undefined;

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) =>
  value === undefined || value === null || value === ""
    ? null
    : (
      <div className="text-sm text-slate-300">
        <span className="text-slate-400">{label}:</span> {String(value)}
      </div>
    );

const toastClass: Record<Toast["kind"], string> = {
  info: "border-cyan-400/30 bg-cyan-500/10 text-cyan-100",
  success: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100",
  error: "border-red-400/30 bg-red-500/10 text-red-100",
};

const ToastViewport = ({
  toasts,
}: {
  toasts: Toast[];
}) => (
  <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`rounded-xl border px-4 py-3 shadow-xl backdrop-blur-md ${
          toastClass[toast.kind]
        }`}
      >
        <div className="flex items-center gap-2">
          {toast.pending && <Spinner className="h-4 w-4" />}
          <div className="text-sm font-semibold">{toast.title}</div>
        </div>
        {toast.description && (
          <div className="mt-1 text-xs opacity-90">{toast.description}</div>
        )}
      </div>
    ))}
  </div>
);

const formatEventDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SourceEventRow = ({ event }: { event: EventEntry }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-1">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="font-medium text-white truncate">
          {event.structured.name}
        </div>
        <div className="text-xs text-gray-400">
          {formatEventDate(event.structured.timestamp.start)}
        </div>
        {event.structured.venue && (
          <div className="text-xs text-gray-500">{event.structured.venue}</div>
        )}
      </div>
      {event.structured.links[0] && (
        <a
          href={event.structured.links[0]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-purple-400 hover:text-purple-300 shrink-0"
        >
          Link
        </a>
      )}
    </div>
  </div>
);

type SelectedSource = {
  label: string;
  sourceKind: "instagram-page" | "whatsapp-group" | "link-scrape-source";
  sourceRef: string;
};

const clusterKey = (request: SourceRequest) =>
  request.kind === "whatsapp-group"
    ? `wa:${stripTrailingNumeral(request.title).toLowerCase()}`
    : request.kind === "facebook-source"
    ? `fb:${request.sourceUrl}`
    : `ig:${request.instagramUsername ?? request.sourceUrl}`;

const clusterRequests = (requests: SourceRequest[]) =>
  Object.values(
    requests.reduce<Record<string, SourceRequest[]>>((acc, request) => {
      const key = clusterKey(request);
      return { ...acc, [key]: [...(acc[key] ?? []), request] };
    }, {}),
  );

const discoverySourceLabel = (source?: string) =>
  source === "spy-bot"
    ? "Spy bot discovery"
    : source === "user-request"
    ? "User request"
    : null;

const fbPayload = (request: SourceRequest) =>
  request.kind === "facebook-source"
    ? (request.payload as {
      title?: string;
      description?: string;
      privacy?: string;
      category?: string;
    })
    : null;

const getSourceRequestViewChatLink = (request: SourceRequest) => {
  const payload = request.payload as Record<string, unknown> | null | undefined;
  const conversationId = request.conversationId ||
    (payload?.conversationId as string | undefined) ||
    (payload?.chatId as string | undefined) ||
    (request.discoverySource === "user-request"
      ? request.requesterUserId
      : undefined);

  const messageId = request.messageId ||
    (payload?.messageId as string | undefined);

  if (!conversationId) return null;
  return viewChatConversationLink(conversationId, messageId);
};

const requestAdminUrl = (requestId: string) => {
  const base = typeof globalThis.location !== "undefined"
    ? globalThis.location.href
    : "https://hi.f0mo.com/admin";
  const url = new URL(base);
  url.pathname = "/admin";
  url.searchParams.set("adminTab", "requests");
  url.searchParams.set("requestId", requestId);
  return url.toString();
};

const targetPendingTab = (request: SourceRequest) =>
  request.discoverySource === "user-request"
    ? "user-requested"
    : whatsappDecision(request) === "Recommended"
    ? "recommended"
    : "skipped";

const RequestDetails = (
  {
    request,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    request: SourceRequest;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const fb = fbPayload(request);
  const viewChatUrl = getSourceRequestViewChatLink(request);
  const isRequestCopied = copiedId === request.id;
  const isUrlCopied = copiedSourceUrl === request.sourceUrl;
  return (
    <>
      <div className="space-y-1">
        <DetailRow label="Created" value={formatDate(request.createdAt)} />
        <DetailRow
          label="Discovery"
          value={discoverySourceLabel(request.discoverySource)}
        />
        <DetailRow
          label="Discovered in"
          value={whatsappChatTitle(request)}
        />
        {request.discoverySource !== "spy-bot" && (
          <DetailRow label="Requester" value={request.requesterUserId} />
        )}
        <DetailRow label="Requester email" value={request.requesterEmail} />
        <div className="text-sm text-slate-300 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-slate-400 shrink-0">Source URL:</span>{" "}
            <a
              href={request.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 underline underline-offset-2 break-all"
            >
              {request.sourceUrl}
            </a>
          </div>
          <button
            type="button"
            onClick={() => onCopySourceUrl(request.sourceUrl)}
            className="text-xs text-slate-400 hover:text-white px-2 py-0.5 rounded border border-white/10 hover:bg-white/5 inline-flex items-center gap-1 shrink-0"
            title="Copy source URL"
          >
            {isUrlCopied
              ? <Check size={12} className="text-emerald-400" />
              : <Copy size={12} />}
            <span>{isUrlCopied ? "Copied URL" : "Copy URL"}</span>
          </button>
        </div>
        {(request.kind === "link-scrape-source" ||
          request.kind === "facebook-source") && (
          <DetailRow label="Source name" value={fb?.title || request.title} />
        )}
        <DetailRow label="Members" value={request.memberCount} />
        {fb?.privacy && <DetailRow label="Privacy" value={fb.privacy} />}
        {fb?.category && <DetailRow label="Category" value={fb.category} />}
        <DetailRow label="AI decision" value={whatsappDecision(request)} />
        <DetailRow label="Recommendation" value={request.recommendation} />
        <DetailRow label="Reviewed" value={formatDate(request.reviewedAt)} />
        <DetailRow label="Reviewer" value={request.reviewerEmail} />
        {request.status === "rejected" && (
          <DetailRow
            label="Rejection Reason"
            value={(request.resolution as
              | { reason?: string; message?: string }
              | undefined)?.reason ||
              (request.resolution as
                | { reason?: string; message?: string }
                | undefined)?.message}
          />
        )}
        <div className="text-sm text-slate-300 flex items-center gap-2 pt-1">
          <span className="text-slate-400">Request link:</span>
          <button
            type="button"
            onClick={() => onCopyLink(request.id)}
            className="inline-flex items-center gap-1.5 text-xs text-purple-300 hover:text-white px-2.5 py-1 rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition"
          >
            {isRequestCopied
              ? <Check size={12} className="text-emerald-400" />
              : <Link size={12} />}
            <span>
              {isRequestCopied ? "Copied request link" : "Copy request link"}
            </span>
          </button>
        </div>
        {viewChatUrl && (
          <div className="text-sm text-slate-300 flex items-center gap-1.5 pt-1">
            <span className="text-slate-400">View Chat:</span>
            <a
              href={viewChatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 underline font-medium"
            >
              Open conversation in View Chat
              <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>
      {fb?.description && (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 text-xs text-slate-300 whitespace-pre-wrap break-words">
          <div className="text-slate-400 font-medium mb-1">
            Group Description:
          </div>
          {fb.description}
        </div>
      )}
      <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-200 whitespace-pre-wrap break-words">
        {request.requestText || "No message text provided."}
      </div>
    </>
  );
};

const ActionButtons = (
  { busy, onApprove, onReject }: {
    busy: boolean;
    onApprove: () => void;
    onReject: (reason?: string) => void;
  },
) => {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [reason, setReason] = useState("");

  if (showRejectInput) {
    return (
      <div className="flex flex-col gap-2 w-full max-w-md">
        <textarea
          placeholder="Reason for rejection (will be sent back to user)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-xl border border-white/20 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          rows={2}
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => {
              setShowRejectInput(false);
              setReason("");
            }}
            className="rounded-xl border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onReject(reason.trim() || undefined);
            }}
            className="flex items-center gap-1.5 rounded-xl bg-red-500/20 px-3 py-1.5 text-xs text-red-100 border border-red-500/30 disabled:opacity-50"
          >
            <X size={14} /> Confirm Reject
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <button
        type="button"
        disabled={busy}
        onClick={onApprove}
        className="flex items-center gap-2 rounded-xl bg-emerald-500/20 px-4 py-2 text-emerald-100 border border-emerald-500/30 disabled:opacity-50"
      >
        <Check size={16} /> Approve
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => setShowRejectInput(true)}
        className="flex items-center gap-2 rounded-xl bg-red-500/20 px-4 py-2 text-red-100 border border-red-500/30 disabled:opacity-50"
      >
        <X size={16} /> Reject
      </button>
    </div>
  );
};

const ClusterCard = (
  {
    cluster,
    busyId,
    onAction,
    targetRequestId,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    cluster: SourceRequest[];
    busyId: string | null;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
      reason?: string,
    ) => void;
    targetRequestId?: string | null;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const isTarget = useMemo(
    () =>
      Boolean(targetRequestId && cluster.some((r) => r.id === targetRequestId)),
    [cluster, targetRequestId],
  );
  const targetIndex = useMemo(() => {
    if (!targetRequestId) return 0;
    const foundIndex = cluster.findIndex((r) => r.id === targetRequestId);
    return foundIndex >= 0 ? foundIndex : 0;
  }, [cluster, targetRequestId]);

  const [expanded, setExpanded] = useState(isTarget);
  const [activeIndex, setActiveIndex] = useState(targetIndex);

  useEffect(() => {
    if (isTarget) {
      setExpanded(true);
      setActiveIndex(targetIndex);
    }
  }, [isTarget, targetIndex]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isTarget && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isTarget]);

  const primary = cluster[activeIndex] ?? cluster[0];
  const status = primary.status as Status;
  return (
    <div
      ref={cardRef}
      className={`rounded-2xl border transition-all duration-300 ${
        isTarget
          ? "border-purple-500/80 bg-purple-950/20 ring-2 ring-purple-500/40"
          : "border-white/10 bg-white/5"
      } backdrop-blur-sm shadow-xl`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/5"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
              {kindLabel(primary.kind)}
              {cluster.length > 1 && ` · ${cluster.length} variants`}
            </div>
            <div className="font-semibold text-white truncate">
              {payloadSummary(primary)}
              {whatsappChatTitle(primary) && (
                <span className="text-xs text-slate-400 font-normal ml-2">
                  from {whatsappChatTitle(primary)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            type="button"
            title="Copy link to requested source"
            onClick={(e) => {
              e.stopPropagation();
              onCopyLink(primary.id);
            }}
            className="p-1.5 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/10 transition flex items-center gap-1.5 text-xs"
          >
            {copiedId === primary.id
              ? <Check size={14} className="text-emerald-400" />
              : <Link size={14} className="text-purple-300" />}
            <span className="hidden sm:inline">
              {copiedId === primary.id ? "Copied" : "Copy link"}
            </span>
          </button>
          {discoverySourceLabel(primary.discoverySource) && (
            <span
              className={`text-xs rounded-full border px-2 py-0.5 ${
                primary.discoverySource === "spy-bot"
                  ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
                  : "border-purple-500/30 bg-purple-500/10 text-purple-200"
              }`}
            >
              {discoverySourceLabel(primary.discoverySource)}
            </span>
          )}
          {primary.memberCount != null && (
            <span className="text-xs text-slate-400">
              {primary.memberCount} members
            </span>
          )}
          <span
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              statusLabelClass[status]
            }`}
          >
            {status}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-5 py-4 space-y-4">
          {cluster.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {cluster.map((r, i) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-lg border px-3 py-1 text-xs ${
                    i === activeIndex
                      ? "border-white/40 bg-white/10 text-white font-medium"
                      : "border-white/10 text-slate-300 hover:bg-white/5"
                  }`}
                >
                  {r.title || r.sourceUrl}
                </button>
              ))}
            </div>
          )}
          <RequestDetails
            request={primary}
            copiedId={copiedId}
            onCopyLink={onCopyLink}
            copiedSourceUrl={copiedSourceUrl}
            onCopySourceUrl={onCopySourceUrl}
          />
          {status === "pending" && (
            <div className="flex items-center justify-between">
              {whatsappDecision(primary) && (
                <span
                  className={`text-xs rounded-full border px-2 py-0.5 ${
                    whatsappDecision(primary) === "Recommended"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  }`}
                >
                  AI: {whatsappDecision(primary)}
                </span>
              )}
              <ActionButtons
                busy={busyId === primary.id}
                onApprove={() => onAction(cluster, primary.id, "approve")}
                onReject={(reason) =>
                  onAction(cluster, primary.id, "reject", reason)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const useAutoLoadNext = (
  canLoadNextPage: boolean,
  loadNextPage: () => void,
) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canLoadNextPage) return;
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) =>
        entries.some((e) => e.isIntersecting) && loadNextPage(),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadNextPage, loadNextPage]);
  return sentinelRef;
};

const pageSize = 20;

const RequestsSection = (
  {
    status,
    busyId,
    hiddenIds,
    onAction,
    recommendationFilter,
    targetRequestId,
    targetRequest,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    status: string;
    busyId: string | null;
    hiddenIds: Set<string>;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
      reason?: string,
    ) => void;
    recommendationFilter?: "user-requested" | "recommended" | "skipped";
    targetRequestId?: string | null;
    targetRequest?: SourceRequest | null;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const { data, isLoading, error, canLoadNextPage, loadNextPage } = db
    .useInfiniteQuery({
      sourceRequests: {
        $: {
          where: { status },
          order: { createdAt: "desc" },
          limit: pageSize,
        },
      },
    });
  const queryRequests = ((data?.sourceRequests ?? []) as SourceRequest[])
    .filter(
      (r) => !hiddenIds.has(r.id),
    );
  const requests = useMemo(() => {
    if (
      targetRequest &&
      targetRequest.status === status &&
      !hiddenIds.has(targetRequest.id) &&
      !queryRequests.some((r) => r.id === targetRequest.id)
    ) {
      return [targetRequest, ...queryRequests];
    }
    return queryRequests;
  }, [queryRequests, targetRequest, status, hiddenIds]);
  const clusters = useMemo(
    () => {
      const raw = status === "pending"
        ? clusterRequests(requests)
        : requests.map((r) => [r]);
      if (!recommendationFilter) return raw;
      return raw.filter((c) => {
        const primary = c[0];
        if (recommendationFilter === "user-requested") {
          return primary.discoverySource === "user-request";
        }
        if (primary.discoverySource === "user-request") {
          return false;
        }
        const decision = whatsappDecision(primary as SourceRequest);
        return recommendationFilter === "recommended"
          ? decision === "Recommended"
          : decision !== "Recommended";
      });
    },
    [requests, status, recommendationFilter],
  );
  const sentinelRef = useAutoLoadNext(canLoadNextPage, loadNextPage);
  const targetBucketLabel = useMemo(() => {
    if (!targetRequestId) return null;
    const targetCluster = clusters.find((c) =>
      c.some((r) => r.id === targetRequestId)
    );
    return targetCluster ? getBucketLabel(targetCluster[0]?.createdAt) : null;
  }, [clusters, targetRequestId]);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    Today: true,
    Yesterday: true,
  });

  useEffect(() => {
    if (targetBucketLabel) {
      setExpandedSections((prev) => ({ ...prev, [targetBucketLabel]: true }));
    }
  }, [targetBucketLabel]);

  const groupedBuckets = useMemo(
    () => groupClustersByDate(clusters),
    [clusters],
  );
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium capitalize text-white">
          {recommendationFilter
            ? recommendationFilter === "user-requested"
              ? "User Requested"
              : recommendationFilter === "recommended"
              ? "AI Recommended"
              : "AI Skipped"
            : status}
        </h2>
        <div className="text-sm text-gray-500">
          {clusters.length} {status === "pending" ? "groups" : "items"}
          {requests.length !== clusters.length && ` (${requests.length} total)`}
        </div>
      </div>
      {error && <div className="text-red-400">{error.message}</div>}
      {isLoading
        ? <Spinner />
        : requests.length === 0
        ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-gray-500">
            No {status} requests.
          </div>
        )
        : (
          <div className="space-y-6">
            {groupedBuckets.map(({ label, clusters: bucketClusters }) => {
              const isExpanded = expandedSections[label] ??
                (label === "Today" || label === "Yesterday");
              return (
                <div key={label} className="space-y-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedSections((prev) => ({
                        ...prev,
                        [label]: !isExpanded,
                      }))}
                    className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition w-full text-left py-1 border-b border-white/5"
                  >
                    {isExpanded
                      ? <ChevronDown size={14} />
                      : <ChevronRight size={14} />}
                    <span>{label}</span>
                    <span className="text-xs text-slate-500 bg-white/5 px-1.5 py-0.5 rounded-full ml-1">
                      {bucketClusters.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="space-y-3">
                      {bucketClusters.map((cluster) => (
                        <ClusterCard
                          key={cluster[0].id}
                          cluster={cluster}
                          busyId={busyId}
                          onAction={onAction}
                          targetRequestId={targetRequestId}
                          copiedId={copiedId}
                          onCopyLink={onCopyLink}
                          copiedSourceUrl={copiedSourceUrl}
                          onCopySourceUrl={onCopySourceUrl}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={sentinelRef} className="h-8" />
            {canLoadNextPage && (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            )}
          </div>
        )}
    </section>
  );
};

export const AdminRequests = () => {
  const user = db.useAuth().user;
  const [searchParams] = useSearchParams();
  const targetRequestId = searchParams.get("requestId");
  const { data: targetData } = db.useQuery(
    targetRequestId
      ? {
        sourceRequests: {
          $: {
            where: { id: targetRequestId },
          },
        },
      }
      : null,
  );
  const targetRequest = (targetData?.sourceRequests?.[0] ?? null) as
    | SourceRequest
    | null;

  const [activeStatus, setActiveStatus] = useState<Status>("pending");
  const [pendingTab, setPendingTab] = useState<
    "user-requested" | "recommended" | "skipped"
  >("user-requested");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedSourceUrl, setCopiedSourceUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!targetRequest) return;
    if (
      targetRequest.status &&
      statuses.includes(targetRequest.status as Status)
    ) {
      setActiveStatus(targetRequest.status as Status);
    }
    if (targetRequest.status === "pending") {
      setPendingTab(targetPendingTab(targetRequest));
    }
  }, [targetRequest]);

  const pushToast = (toast: Omit<Toast, "id">) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((current) => [...current, { ...toast, id }]);
    if (!toast.pending) {
      setTimeout(
        () => setToasts((current) => current.filter((item) => item.id !== id)),
        3500,
      );
    }
    return id;
  };

  const replaceToast = (id: number, toast: Omit<Toast, "id">) => {
    setToasts((current) =>
      current.map((item) => item.id === id ? { ...toast, id } : item)
    );
    setTimeout(
      () => setToasts((current) => current.filter((item) => item.id !== id)),
      3500,
    );
  };

  const copyLink = (requestId: string) => {
    const url = requestAdminUrl(requestId);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(requestId);
      setTimeout(() => setCopiedId(null), 2000);
      pushToast({
        kind: "success",
        title: "Link copied to clipboard",
        description: url,
      });
    });
  };

  const copySourceUrl = (sourceUrl: string) => {
    navigator.clipboard.writeText(sourceUrl).then(() => {
      setCopiedSourceUrl(sourceUrl);
      setTimeout(() => setCopiedSourceUrl(null), 2000);
      pushToast({
        kind: "success",
        title: "Source URL copied to clipboard",
        description: sourceUrl,
      });
    });
  };

  const act = async (
    cluster: SourceRequest[],
    requestId: string,
    action: "approve" | "reject",
    reason?: string,
  ) => {
    const auth = await db.getAuth();
    const token = auth?.refresh_token;
    if (!token) {
      setMessage("Missing auth token");
      return;
    }
    const clusterIds = cluster.map((r) => r.id);
    setHiddenIds((prev) => new Set([...prev, ...clusterIds]));
    setBusyId(requestId);
    setMessage("");
    const toastId = pushToast({
      kind: "info",
      pending: true,
      title: action === "approve" ? "Approving request" : "Rejecting request",
    });
    const promise = action === "approve"
      ? approveAdminSourceRequest(token, requestId)
      : rejectAdminSourceRequest(token, requestId, reason);
    promise
      .then(() =>
        replaceToast(toastId, {
          kind: "success",
          title: action === "approve" ? "Request approved" : "Request rejected",
        })
      )
      .catch((err: unknown) => {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          clusterIds.forEach((id) => next.delete(id));
          return next;
        });
        const errorMessage = err instanceof Error
          ? err.message
          : `Failed to ${action} request`;
        setMessage(errorMessage);
        replaceToast(toastId, {
          kind: "error",
          title: action === "approve"
            ? "Failed to approve request"
            : "Failed to reject request",
          description: errorMessage,
        });
      })
      .finally(() => setBusyId(null));
  };

  if (!user) return <Spinner />;

  return (
    <div className="space-y-6">
      <ToastViewport toasts={toasts} />
      {message && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
          {message}
        </div>
      )}
      <div className="flex gap-2">
        {statuses.map((status) => (
          <button
            type="button"
            key={status}
            onClick={() => setActiveStatus(status)}
            className={`rounded-xl border px-4 py-2 text-sm capitalize ${
              activeStatus === status
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            {status}
          </button>
        ))}
      </div>
      {activeStatus === "pending" && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPendingTab("user-requested")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "user-requested"
                ? "border-purple-500/30 bg-purple-500/10 text-purple-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            User Requested
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("recommended")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "recommended"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Recommended
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("skipped")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "skipped"
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Skipped
          </button>
        </div>
      )}
      <RequestsSection
        key={`${activeStatus}-${pendingTab}`}
        status={activeStatus}
        busyId={busyId}
        hiddenIds={hiddenIds}
        onAction={act}
        recommendationFilter={activeStatus === "pending"
          ? pendingTab
          : undefined}
        targetRequestId={targetRequestId}
        targetRequest={targetRequest}
        copiedId={copiedId}
        onCopyLink={copyLink}
        copiedSourceUrl={copiedSourceUrl}
        onCopySourceUrl={copySourceUrl}
      />
    </div>
  );
};

const useSourceEvents = (selectedSource: SelectedSource | null) => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSource) return;
    const mounted = { current: true };
    setLoading(true);
    setError(null);
    db.getAuth()
      .then((auth) => {
        if (!auth?.refresh_token) throw new Error("Missing auth token");
        return fetchAdminSourceEvents(
          auth.refresh_token,
          selectedSource.sourceKind,
          selectedSource.sourceRef,
        );
      })
      .then((result) => {
        if (mounted.current) setEvents(result);
      })
      .catch((err) => {
        if (mounted.current) {
          setError(
            err instanceof Error ? err.message : "Failed loading events",
          );
        }
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, [selectedSource?.sourceKind, selectedSource?.sourceRef]);

  return { events, loading, error };
};

const ExpandableSourceCard = (
  { selectedSource, header }: {
    selectedSource: SelectedSource;
    header: React.ReactNode;
  },
) => {
  const [expanded, setExpanded] = useState(false);
  const { events, loading, error } = useSourceEvents(
    expanded ? selectedSource : null,
  );
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/10 transition"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <div className="min-w-0 flex-1">{header}</div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-4 py-3 space-y-2 bg-slate-950/40">
          {loading
            ? <Spinner className="h-4 w-4" />
            : error
            ? <div className="text-red-400 text-sm">{error}</div>
            : events.length === 0
            ? (
              <div className="text-gray-500 text-sm">
                No ingested events yet.
              </div>
            )
            : (
              <>
                <div className="text-xs text-gray-500">
                  {events.length} events
                </div>
                {events.map((event) => (
                  <SourceEventRow key={event.id} event={event} />
                ))}
              </>
            )}
        </div>
      )}
    </div>
  );
};

const CoverageColumn = (
  { title, icon, count, children }: {
    title: string;
    icon: React.ReactNode;
    count: number;
    children: React.ReactNode;
  },
) => (
  <section className="space-y-3 min-w-0">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl font-medium text-white">{title}</h2>
      </div>
      <div className="text-sm text-gray-500">{count}</div>
    </div>
    {children}
  </section>
);

export const AdminCoverage = () => {
  const { data, isLoading, error } = db.useQuery({
    instagramSources: { $: { where: { status: "active" } } },
    whatsappGroups: {},
    linkScrapeSources: { $: { where: { status: "active" } } },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-red-400">
        Error loading sources: {error.message}
      </div>
    );
  }

  const instagram = data?.instagramSources ?? [];
  const whatsapp = data?.whatsappGroups ?? [];
  const linkSources = data?.linkScrapeSources ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <CoverageColumn
        title="Instagram Pages"
        icon={<Globe size={20} className="text-purple-400" />}
        count={instagram.length}
      >
        {instagram.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No active Instagram sources.
            </div>
          )
          : (
            <div className="space-y-2">
              {instagram.map((s) => (
                <ExpandableSourceCard
                  key={s.id}
                  selectedSource={{
                    label: `@${s.username}`,
                    sourceKind: "instagram-page",
                    sourceRef: s.url ??
                      `https://www.instagram.com/${s.username}`,
                  }}
                  header={
                    <>
                      <div className="font-medium text-white truncate">
                        @{s.username}
                      </div>
                      {s.categories && s.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {s.categories.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        added by {s.addedBy}
                      </div>
                    </>
                  }
                />
              ))}
            </div>
          )}
      </CoverageColumn>

      <CoverageColumn
        title="WhatsApp Groups"
        icon={<MessageCircle size={20} className="text-emerald-400" />}
        count={whatsapp.length}
      >
        {whatsapp.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No WhatsApp groups.
            </div>
          )
          : (
            <div className="space-y-2">
              {whatsapp.map((g) =>
                g.whatsappId
                  ? (
                    <ExpandableSourceCard
                      key={g.id}
                      selectedSource={{
                        label: g.name ?? g.whatsappId,
                        sourceKind: "whatsapp-group",
                        sourceRef: g.whatsappId,
                      }}
                      header={
                        <>
                          <div className="font-medium text-white truncate">
                            {g.name ?? g.whatsappId}
                          </div>
                          {g.memberCount != null && (
                            <div className="text-xs text-gray-400 mt-1">
                              {g.memberCount} members
                            </div>
                          )}
                          {g.description && (
                            <div className="text-xs text-gray-500 line-clamp-2 mt-1">
                              {g.description}
                            </div>
                          )}
                        </>
                      }
                    />
                  )
                  : null
              )}
            </div>
          )}
      </CoverageColumn>

      <CoverageColumn
        title="Link Sources"
        icon={<Globe size={20} className="text-cyan-400" />}
        count={linkSources.length}
      >
        {linkSources.length === 0
          ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
              No active link sources.
            </div>
          )
          : (
            <div className="space-y-2">
              {linkSources.map((s) => (
                <ExpandableSourceCard
                  key={s.id}
                  selectedSource={{
                    label: s.source,
                    sourceKind: "link-scrape-source",
                    sourceRef: s.source,
                  }}
                  header={
                    <>
                      <div className="font-medium text-white truncate">
                        {s.source}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-1">
                        {s.url}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.useProxy && (
                          <span className="rounded-full bg-cyan-500/20 border border-cyan-500/30 px-2 py-0.5 text-xs text-cyan-200">
                            proxy
                          </span>
                        )}
                        {s.categories?.map((c) => (
                          <span
                            key={c}
                            className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </>
                  }
                />
              ))}
            </div>
          )}
      </CoverageColumn>
    </div>
  );
};
